const emTagRegex = /<em>(.*?)<\/em>/gi;
const GERMAN_SEARCH_REPLACEMENTS = {
  '\u00E4': 'ae',
  '\u00F6': 'oe',
  '\u00FC': 'ue',
  '\u00DF': 'ss',
};

const normalizeTerm = (value) => {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
};

const normalizeSearchValue = (value) => {
  return value
    .toLowerCase()
    .split('')
    .map(character => GERMAN_SEARCH_REPLACEMENTS[character] || character)
    .join('');
};

const buildNormalizedSearchText = (text) => {
  const normalizedToSourceIndex = [];
  const sourceToNormalizedIndex = [];
  const boundaries = new Set([0]);
  let normalizedText = '';

  for (let sourceIndex = 0; sourceIndex < text.length; sourceIndex += 1) {
    sourceToNormalizedIndex[sourceIndex] = normalizedText.length;
    const normalizedChunk = normalizeSearchValue(text[sourceIndex]);
    normalizedText += normalizedChunk;
    for (let chunkIndex = 0; chunkIndex < normalizedChunk.length; chunkIndex += 1) {
      normalizedToSourceIndex.push(sourceIndex);
    }
    boundaries.add(normalizedText.length);
  }

  sourceToNormalizedIndex[text.length] = normalizedText.length;

  return {
    normalizedText,
    normalizedToSourceIndex,
    sourceToNormalizedIndex,
    boundaries,
  };
};

const findTermMatch = (normalizedText, term, cursor, boundaries) => {
  let searchFrom = cursor;

  while (searchFrom <= normalizedText.length - term.length) {
    const index = normalizedText.indexOf(term, searchFrom);
    if (index === -1) {
      return null;
    }

    const endIndex = index + term.length;
    if (boundaries.has(index) && boundaries.has(endIndex)) {
      return { index, term };
    }

    searchFrom = index + 1;
  }

  return null;
};

export const extractSearchHighlightTerms = (highlights = []) => {
  if (!Array.isArray(highlights)) {
    return [];
  }

  const terms = [];
  highlights.forEach((highlight) => {
    if (typeof highlight !== 'string') {
      return;
    }

    let match;
    while ((match = emTagRegex.exec(highlight)) !== null) {
      const normalized = normalizeTerm(match[1]);
      if (normalized) {
        terms.push(normalized);
      }
    }
    emTagRegex.lastIndex = 0;
  });

  return terms;
};

export const buildSearchHighlightTerms = (searchQuery, highlights = [], extraTerms = []) => {
  const uniqueTerms = [];
  const seenKeys = new Set();
  const allTerms = [searchQuery, ...extraTerms, ...extractSearchHighlightTerms(highlights)];

  allTerms.forEach((term) => {
    const normalized = normalizeTerm(term);
    if (!normalized) {
      return;
    }

    const key = normalizeSearchValue(normalized);
    if (seenKeys.has(key)) {
      return;
    }
    seenKeys.add(key);
    uniqueTerms.push(normalized);
  });

  return uniqueTerms.sort((left, right) => right.length - left.length);
};

export const splitTextBySearchTerms = (text, terms = []) => {
  if (typeof text !== 'string') {
    return [];
  }

  if (!Array.isArray(terms) || terms.length === 0) {
    return [{ text, highlighted: false }];
  }

  const seenTerms = new Set();
  const normalizedTerms = terms
    .map((term) => {
      return normalizeSearchValue(normalizeTerm(term));
    })
    .filter((term) => {
      if (term.length === 0 || seenTerms.has(term)) {
        return false;
      }
      seenTerms.add(term);
      return true;
    })
    .sort((left, right) => right.length - left.length);

  if (normalizedTerms.length === 0) {
    return [{ text, highlighted: false }];
  }

  const {
    normalizedText,
    normalizedToSourceIndex,
    sourceToNormalizedIndex,
    boundaries,
  } = buildNormalizedSearchText(text);
  const segments = [];
  let sourceCursor = 0;
  let normalizedCursor = 0;

  while (sourceCursor < text.length) {
    let nextMatch = null;

    for (let termIndex = 0; termIndex < normalizedTerms.length; termIndex += 1) {
      const term = normalizedTerms[termIndex];
      const match = findTermMatch(normalizedText, term, normalizedCursor, boundaries);
      if (!match) {
        continue;
      }

      if (!nextMatch || match.index < nextMatch.index || (match.index === nextMatch.index && term.length > nextMatch.term.length)) {
        nextMatch = match;
      }
    }

    if (!nextMatch) {
      segments.push({
        text: text.slice(sourceCursor),
        highlighted: false,
      });
      break;
    }

    const matchStartSourceIndex = normalizedToSourceIndex[nextMatch.index];
    const matchEndSourceIndex = normalizedToSourceIndex[nextMatch.index + nextMatch.term.length - 1] + 1;

    if (matchStartSourceIndex > sourceCursor) {
      segments.push({
        text: text.slice(sourceCursor, matchStartSourceIndex),
        highlighted: false,
      });
    }

    segments.push({
      text: text.slice(matchStartSourceIndex, matchEndSourceIndex),
      highlighted: true,
    });

    sourceCursor = matchEndSourceIndex;
    normalizedCursor = sourceToNormalizedIndex[sourceCursor];
  }

  return segments.filter(segment => segment.text.length > 0);
};

export const textMatchesSearchTerms = (text, terms = []) => {
  if (typeof text !== 'string' || !Array.isArray(terms)) {
    return false;
  }

  const { normalizedText, boundaries } = buildNormalizedSearchText(text);
  return terms.some((term) => {
    const normalized = normalizeSearchValue(normalizeTerm(term));
    return normalized.length > 0 && Boolean(findTermMatch(normalizedText, normalized, 0, boundaries));
  });
};
